import type { LabelRecordMap } from '@libs/data-access/entities'
import type {
  ShiftRecord,
  ApiErrorResponseData
} from '@libs/data-access/queries'
import { AlertErrorAdapter, Divider, Modal, TextArea, Title } from '@libs/ui/ds'
import { BaseForm } from '@libs/ui/form'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatErrors } from '@spa/components/FormFields/helpers'
import formStyles from '@spa/components/FormFields/styles/Form.module.css'
import { useMemoSelector } from '@spa/redux/hooks'
import { LockedAlert } from '@spa/scenes/Plannings/Forms/components'
import React from 'react'
import type { FC } from 'react'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { getContractById } from 'snap-redux/modules/contracts/selectors'
import { updateShift } from 'snap-redux/modules/shifts'
import { bindPromiseActionCreators } from 'snap-redux/utils'

import {
  BreakRowsContainer,
  ShiftTimeRangeInputs
} from '../../ShiftForm/components'
import { MealRowsContainerLegacy } from '../../ShiftForm/components/MealRowsContainer/MealRowsContainerLegacy'
import { LabelSelectLegacy } from '../../components/LabelField/LabelSelectLegacy'
import {
  HeaderInfo,
  ModalActionFooterLegacy,
  ModalRealHoursTitle
} from '../components'
import { EmployeeNotAllowed } from '../components/EmployeeNotAllowed/EmployeeNotAllowed'
import { useRealFormInitLegacy } from '../hooks/useRealFormInitLegacy'
import type { CommonFormRealDataLegacy } from '../models/CommonFormRealData'

import styles from './UnplannedShiftRealHoursForm.module.css'
import { UnplannedBlockHoursInfoLegacy } from './components/UnplannedBlockHoursInfo/UnplannedBlockHoursInfoLegacy'
import useUnplannedShiftRealHoursFormLegacy from './hooks/useUnplannedShiftRealHoursFormLegacy'

type UnplannedShiftRealHoursProps = {
  shift?: ShiftRecord
  formSuccess: () => void
  currentUserId: number
}

/**
 * @deprecated file
 */

export const UnplannedShiftRealHoursFormLegacy: FC<
  UnplannedShiftRealHoursProps
> = ({ shift: shiftData, currentUserId, formSuccess }) => {
  const { t } = usePlanningFormTranslation()
  const dispatch = useDispatch()

  const {
    textFields,
    setTextField,
    formLoading,
    setFormLoading,
    formError,
    setFormError,
    shift,
    contractLocationId,
    isManager,
    isDirector,
    employeesCanSetRealHours,
    labels,
    labelColor,
    defaultLocationMeal,
    padType,
    hasSnapPadBreaksEnable,
    breakAsReal,
    shiftIsLocked,
    breakArePaid,
    canValidate,
    employeesCanModifyMeal,
    getCommonShiftPayload,
    onDeleteSubmit
  } = useRealFormInitLegacy({ shiftData, formSuccess, currentUserId })

  const methods = useUnplannedShiftRealHoursFormLegacy({
    shift,
    breakAsReal,
    padType
  })

  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = methods

  const { shift_time_range } = watch()

  const contract = useMemoSelector(state =>
    getContractById(state, shift.user_contract_id)
  )

  if (!isManager && !employeesCanSetRealHours) {
    return <EmployeeNotAllowed />
  }

  const handleRealShiftNoteChange = value => {
    setTextField({
      ...textFields,
      shift: { ...textFields.shift, real_note: value }
    })
  }

  const bindedActions = bindPromiseActionCreators({ updateShift }, dispatch)

  const onSubmit = (formData: CommonFormRealDataLegacy) => {
    const shiftData = getCommonShiftPayload(formData)

    setFormLoading(true)

    bindedActions
      .updateShift(shiftData.id, shiftData)
      .then(() => formSuccess())
      .catch((error: ApiErrorResponseData) => {
        setFormLoading(false)
        setFormError(error)
      })
  }

  return (
    <>
      <ModalRealHoursTitle
        isValidation={isManager}
        shiftIsLocked={shiftIsLocked}
      />
      <BaseForm
        methods={methods}
        onSubmit={onSubmit}
        className='flex grow flex-col'
      >
        <Modal.Content>
          {shiftIsLocked && <LockedAlert />}
          <AlertErrorAdapter className='mb-5' error={formError} />

          <HeaderInfo
            membershipId={contract?.membership_id}
            date={shift.starts_at}
          />
          <UnplannedBlockHoursInfoLegacy
            shift={shift}
            color={labelColor}
            isManager={isManager}
            hasSnapPadBreakEnable={hasSnapPadBreaksEnable}
          />
          <Divider />
          <Title size={3} className={styles.RealHoursTitle}>
            {t('realHoursForm.realHours')}
          </Title>
          {isManager && (
            <LabelSelectLegacy
              control={control}
              errors={errors}
              disabled={shiftIsLocked}
              haveAccess={isDirector}
              labels={labels as LabelRecordMap}
              selectedLocationId={contractLocationId}
              setValue={setValue}
            />
          )}
          <div className={styles.groupStartAndEnd}>
            <ShiftTimeRangeInputs
              // Forcing any type here even though it shouldn't be needed...
              control={control as Control<any>}
              errors={errors}
              disabled={shiftIsLocked}
            />
          </div>

          <BreakRowsContainer
            shiftTimeRange={shift_time_range}
            isShiftLocked={shiftIsLocked}
            isBreakPaid={breakArePaid}
            isManager={isManager}
          />
          {(isManager || (!isManager && Boolean(shift.shift_meals?.size))) && (
            <MealRowsContainerLegacy
              control={control}
              defaultSelectedOption={defaultLocationMeal}
              isShiftLocked={shiftIsLocked}
              isManager={isManager}
              canEmployeeEdit={employeesCanModifyMeal}
              real
            />
          )}
          <div className={formStyles.FieldGroup}>
            <Controller
              name='note'
              control={control}
              render={() => (
                <TextArea
                  errorMessage={formatErrors({
                    inputName: 'note',
                    errors
                  })}
                  label={t('realHoursForm.notes')}
                  name='note'
                  onChange={e => handleRealShiftNoteChange(e.target.value)}
                  value={textFields.shift.real_note}
                  disabled={shiftIsLocked}
                />
              )}
            />
          </div>
        </Modal.Content>
        <ModalActionFooterLegacy
          shift={shift}
          shiftIsLocked={shiftIsLocked}
          canValidate={canValidate}
          deleteCta={onDeleteSubmit}
          isLoading={formLoading}
          isManager={isManager}
        />
      </BaseForm>
    </>
  )
}
